/** API Playground endpoint catalog — all public REST docs endpoints. */

export const PLAYGROUND_API_BASE =
  (process.env.REACT_APP_API_BASE_URL || "https://api.ibrlive.com")
    .replace(/\/+$/, "")
    .replace(/\/api$/i, "");

const apiKeyParam = {
  name: "apiKey",
  required: true,
  description: "Your API key (auto-filled when logged in)",
};

/** @typedef {{ key: string, label: string, defaultValue?: string, description?: string }} PlaygroundParam */

/**
 * accessFeature maps to planApiAccess PLAN_ACCESS keys.
 * playground: false = Details only (e.g. WebSocket).
 */
export const PLAYGROUND_ENDPOINTS = [
  {
    id: "snapshot-all",
    group: "Forex Snapshot",
    name: "Forex Snapshot — All Pairs",
    shortName: "All pairs",
    method: "GET",
    minPlan: "FREE",
    accessFeature: "snapshot_all",
    playground: true,
    description:
      "Live bid/ask quotes and last trades for all forex pairs. Cost: ceil(total pairs ÷ 5).",
    pathTemplate: "/api/forex/snapshot",
    buildPath: () => "/api/forex/snapshot",
    pathParams: [],
    queryParams: [],
    sampleOutput: {
      success: true,
      lastQuotes: [{ s: "USDINR", a: 83.58, b: 83.56, t: 1704123456789 }],
      lastTrades: [{ s: "USDINR", p: 83.57, t: 1704123456789 }],
    },
    inputParams: [apiKeyParam],
    outputParams: [
      { name: "lastQuotes", description: "Array of quote objects (a/b/s/t)" },
      { name: "lastTrades", description: "Array of last trade objects" },
    ],
    responseGuide: [
      { name: "success", meaning: "true if the request succeeded" },
      { name: "user", meaning: "Email of the API key owner" },
      { name: "lastQuotes[].s", meaning: "Currency pair symbol, e.g. USDINR" },
      { name: "lastQuotes[].a", meaning: "Ask (sell) price" },
      { name: "lastQuotes[].b", meaning: "Bid (buy) price" },
      { name: "lastQuotes[].t", meaning: "Unix timestamp in milliseconds" },
      { name: "lastQuotes[].t_ist", meaning: "Same time in IST (ISO string)" },
      { name: "lastTrades[].p", meaning: "Last traded price" },
    ],
  },
  {
    id: "snapshot-single",
    group: "Forex Snapshot",
    name: "Forex Snapshot — Single Pair",
    shortName: "Single pair",
    method: "GET",
    minPlan: "FREE",
    accessFeature: "snapshot_single",
    playground: true,
    description: "Live bid/ask and last trade for one pair. Cost: 1 call.",
    pathTemplate: "/api/forex/snapshot/{symbol}",
    buildPath: ({ symbol }) =>
      `/api/forex/snapshot/${encodeURIComponent(symbol || "USDINR")}`,
    pathParams: [
      {
        key: "symbol",
        label: "symbol",
        defaultValue: "USDINR",
        description: "Forex pair",
      },
    ],
    queryParams: [],
    sampleOutput: {
      success: true,
      quote: { s: "USDINR", a: 83.58, b: 83.56 },
      trade: { s: "USDINR", p: 83.57 },
    },
    inputParams: [
      { name: "symbol", required: true, description: "Forex pair path param" },
      apiKeyParam,
    ],
    outputParams: [
      { name: "quote", description: "Bid/ask quote object" },
      { name: "trade", description: "Last trade object" },
    ],
    responseGuide: [
      { name: "success", meaning: "true if the request succeeded" },
      { name: "quote.s", meaning: "Currency pair symbol" },
      { name: "quote.a", meaning: "Ask (sell) price" },
      { name: "quote.b", meaning: "Bid (buy) price" },
      { name: "quote.t / t_ist", meaning: "Timestamp (ms) and IST string" },
      { name: "trade.p", meaning: "Last traded price" },
    ],
  },
  {
    id: "snapshot-batch",
    group: "Forex Snapshot",
    name: "Forex Snapshot — Batch",
    shortName: "Batch / multi",
    method: "GET",
    minPlan: "FREE",
    accessFeature: "snapshot_multi",
    playground: true,
    description:
      "Live quotes for multiple pairs. Cost: 1 call per pair requested.",
    pathTemplate: "/api/forex/snapshot/symbols",
    buildPath: () => "/api/forex/snapshot/symbols",
    pathParams: [],
    queryParams: [
      {
        key: "symbols",
        label: "symbols",
        defaultValue: "USDINR,EURUSD,GBPINR",
        description: "Comma-separated pairs",
      },
    ],
    sampleOutput: {
      success: true,
      lastQuotes: [
        { s: "USDINR", a: 83.58, b: 83.56 },
        { s: "EURUSD", a: 1.0852, b: 1.085 },
      ],
    },
    inputParams: [
      {
        name: "symbols",
        required: true,
        description: "Comma-separated pairs",
      },
      apiKeyParam,
    ],
    outputParams: [
      { name: "lastQuotes", description: "Array of quote objects" },
      { name: "lastTrades", description: "Array of last trade objects" },
    ],
    responseGuide: [
      { name: "success", meaning: "true if the request succeeded" },
      { name: "user", meaning: "Email of the API key owner" },
      { name: "lastQuotes[].s", meaning: "Currency pair symbol, e.g. USDINR" },
      { name: "lastQuotes[].a", meaning: "Ask (sell) price" },
      { name: "lastQuotes[].b", meaning: "Bid (buy) price" },
      { name: "lastQuotes[].t", meaning: "Unix timestamp in milliseconds" },
      { name: "lastQuotes[].t_ist", meaning: "Same time in IST (ISO string)" },
      { name: "lastTrades[].p", meaning: "Last traded price" },
      { name: "lastTrades[].s / t / t_ist", meaning: "Same meaning as in quotes" },
    ],
  },
  {
    id: "conversion",
    group: "FX Tools",
    name: "Currency Conversion",
    shortName: "Conversion",
    method: "GET",
    minPlan: "BASIC",
    accessFeature: "currency_conversion",
    playground: true,
    description:
      "Convert an amount between two currencies using mid rates from snapshot cache.",
    pathTemplate: "/api/forex/convert",
    buildPath: () => "/api/forex/convert",
    pathParams: [],
    queryParams: [
      { key: "from", label: "from", defaultValue: "USD" },
      { key: "to", label: "to", defaultValue: "INR" },
      { key: "amount", label: "amount", defaultValue: "100" },
    ],
    sampleOutput: {
      success: true,
      from: "USD",
      to: "INR",
      amount: 100,
      rate: 83.57,
      converted_amount: 8357,
    },
    inputParams: [
      { name: "from", required: true, description: "Source ISO currency" },
      { name: "to", required: true, description: "Target ISO currency" },
      { name: "amount", required: true, description: "Amount to convert" },
      apiKeyParam,
    ],
    outputParams: [
      { name: "rate", description: "Applied mid rate" },
      { name: "converted_amount", description: "Resulting amount" },
    ],
    responseGuide: [
      { name: "success", meaning: "true if conversion succeeded" },
      { name: "from / to", meaning: "Source and target currency codes" },
      { name: "amount", meaning: "Original amount you sent" },
      { name: "rate", meaning: "Mid rate used for conversion" },
      { name: "converted_amount", meaning: "amount × rate" },
    ],
  },
  {
    id: "aggregates",
    group: "FX Tools",
    name: "Forex Aggregates",
    shortName: "Aggregates",
    method: "GET",
    minPlan: "BASIC",
    accessFeature: "aggregate",
    playground: true,
    description:
      "Historical OHLCV bars. ≤6-month from/to in one call; longer ranges use page=1..N (6 months per page).",
    pathTemplate: "/api/aggregate/data",
    buildPath: () => "/api/aggregate/data",
    pathParams: [],
    queryParams: [
      { key: "symbol", label: "symbol", defaultValue: "USDINR" },
      { key: "time", label: "time", defaultValue: "day" },
      { key: "multiply", label: "multiply", defaultValue: "1" },
      { key: "from", label: "from", defaultValue: "2020-01-01" },
      { key: "to", label: "to", defaultValue: "2026-04-30" },
      { key: "page", label: "page", defaultValue: "1" },
    ],
    sampleOutput: {
      success: true,
      symbol: "USDINR",
      from: "2020-01-01",
      to: "2020-06-30",
      pagination: {
        page: 1,
        totalPages: 13,
        maxMonths: 6,
        hasNext: true,
        nextPage: 2,
      },
      data: { results: [{ o: 83.45, h: 83.67, l: 83.32, c: 83.58, v: 1250000 }] },
    },
    inputParams: [
      { name: "symbol", required: true, description: "Forex pair" },
      { name: "time", required: true, description: "day | month | quarter | year" },
      { name: "from", required: true, description: "YYYY-MM-DD" },
      { name: "to", required: true, description: "YYYY-MM-DD; >6 months needs page" },
      { name: "page", required: false, description: "1-based page (default 1)" },
      apiKeyParam,
    ],
    outputParams: [
      { name: "data.results", description: "OHLCV bar array for this page" },
      { name: "pagination", description: "page, totalPages, hasNext, nextPage" },
    ],
    responseGuide: [
      { name: "o / h / l / c", meaning: "Open / High / Low / Close for the bar" },
      { name: "v", meaning: "Volume" },
      { name: "t", meaning: "Bar timestamp (Unix ms)" },
      { name: "recordCount", meaning: "How many bars returned" },
    ],
  },
  {
    id: "technical",
    group: "FX Tools",
    name: "Technical Indicators",
    shortName: "Technical",
    method: "GET",
    minPlan: "ULTIMATE",
    accessFeature: "technical_indicators",
    playground: true,
    description: "RSI, MACD, SMA/EMA, Bollinger, Stochastic — Ultimate only.",
    pathTemplate: "/api/forex/get-technical-indicators",
    buildPath: () => "/api/forex/get-technical-indicators",
    pathParams: [],
    queryParams: [
      { key: "symbol", label: "symbol", defaultValue: "USDINR" },
      { key: "timeframe", label: "timeframe", defaultValue: "daily" },
    ],
    sampleOutput: {
      success: true,
      symbol: "USDINR",
      timeframe: "daily",
      data: {},
    },
    inputParams: [
      { name: "symbol", required: true, description: "Forex pair" },
      { name: "timeframe", required: false, description: "daily | weekly" },
      apiKeyParam,
    ],
    outputParams: [{ name: "data", description: "Indicator payload" }],
  },
  {
    id: "prevclose",
    group: "FX Tools",
    name: "Previous Close",
    shortName: "Previous Close",
    method: "GET",
    minPlan: "BASIC",
    accessFeature: "previous_close",
    playground: true,
    description: "Previous trading day's OHLCV for a pair.",
    pathTemplate: "/api/forex/previous-close",
    buildPath: () => "/api/forex/previous-close",
    pathParams: [],
    queryParams: [
      { key: "symbol", label: "symbol", defaultValue: "USDINR" },
    ],
    sampleOutput: {
      success: true,
      symbol: "USDINR",
      data: { open: 83.42, high: 83.68, low: 83.28, close: 83.55 },
    },
    inputParams: [
      { name: "symbol", required: true, description: "Forex pair" },
      apiKeyParam,
    ],
    outputParams: [{ name: "data", description: "OHLCV object" }],
  },
  {
    id: "customs",
    group: "Reference Rates",
    name: "Indian Customs Rates",
    shortName: "Customs",
    method: "GET",
    minPlan: "ESSENTIAL",
    accessFeature: "reference_rates",
    playground: true,
    description: "ICEGATE-aligned customs valuation rates (Mon–Fri 6:40 PM IST).",
    pathTemplate: "/api/forex/reference-rates/indian-customs",
    buildPath: () => "/api/forex/reference-rates/indian-customs",
    pathParams: [],
    queryParams: [],
    sampleOutput: {
      meta: { row_count: 22 },
      data: [{ "Currency Code": "AED", "Import Rate": "26.1" }],
    },
    inputParams: [apiKeyParam],
    outputParams: [
      { name: "meta", description: "Row count metadata" },
      { name: "data", description: "Customs rate rows" },
    ],
  },
  {
    id: "rbi-latest",
    group: "Reference Rates",
    name: "RBI Rates (Latest)",
    shortName: "RBI Latest",
    method: "GET",
    minPlan: "ESSENTIAL",
    accessFeature: "reference_rates",
    playground: true,
    description: "Most recently published RBI reference rates (6 pairs).",
    pathTemplate: "/api/forex/reference-rates/rbi-latest",
    buildPath: () => "/api/forex/reference-rates/rbi-latest",
    pathParams: [],
    queryParams: [],
    sampleOutput: {
      data: [{ date: "04/09/2026", "USD/INR": "94.4914", "AED/INR": "25.7272" }],
    },
    inputParams: [apiKeyParam],
    outputParams: [{ name: "data", description: "Latest RBI rate row(s)" }],
  },
  {
    id: "dxy",
    group: "Reference Rates",
    name: "DXY Index",
    shortName: "DXY",
    method: "GET",
    minPlan: "ESSENTIAL",
    accessFeature: "dxy",
    playground: true,
    description: "US Dollar Index last value.",
    pathTemplate: "/api/last/index/DXY",
    buildPath: () => "/api/last/index/DXY",
    pathParams: [],
    queryParams: [],
    sampleOutput: {
      symbol: "DXY",
      price: 99.1852,
      timestamp: 1774339583085,
    },
    inputParams: [apiKeyParam],
    outputParams: [
      { name: "price", description: "Index value" },
      { name: "timestamp", description: "Unix ms" },
    ],
  },
  {
    id: "rbi-hist",
    group: "Reference Rates",
    name: "RBI Rates (Historical)",
    shortName: "RBI Historical",
    method: "GET",
    minPlan: "BASIC",
    accessFeature: "rbi_historical",
    playground: true,
    description: "Historical RBI reference rates by date range (DD-MM-YYYY).",
    pathTemplate: "/api/forex/reference-rates/rbi-historical",
    buildPath: () => "/api/forex/reference-rates/rbi-historical",
    pathParams: [],
    queryParams: [
      { key: "from", label: "from", defaultValue: "01-09-2026" },
      { key: "to", label: "to", defaultValue: "16-09-2026" },
    ],
    sampleOutput: {
      data: [{ date: "15/09/2026", "USD/INR": "95.9253" }],
    },
    inputParams: [
      { name: "from", required: true, description: "DD-MM-YYYY" },
      { name: "to", required: true, description: "DD-MM-YYYY" },
      apiKeyParam,
    ],
    outputParams: [{ name: "data", description: "Historical rate rows" }],
  },
  {
    id: "forward",
    group: "Forward Rates",
    name: "Forward Rate (Broken Date)",
    shortName: "Forward Rate",
    method: "GET",
    minPlan: "PRO",
    accessFeature: "forward_rates",
    playground: true,
    description:
      "Broken-date forward FX rate. Requires Pro/Ultimate Forward Rate add-on.",
    pathTemplate: "/api/forex/forward-rate",
    buildPath: () => "/api/forex/forward-rate",
    pathParams: [],
    queryParams: [
      { key: "pair", label: "pair", defaultValue: "USDINR" },
      { key: "date", label: "date", defaultValue: "2026-12-31" },
    ],
    sampleOutput: {
      pair: "USDINR",
      spot: { bid: 94.657, ask: 94.66 },
      forward: { bid: 95.0236, ask: 95.0466 },
    },
    inputParams: [
      { name: "pair", required: true, description: "e.g. USDINR" },
      { name: "date", required: true, description: "YYYY-MM-DD settlement" },
      apiKeyParam,
    ],
    outputParams: [
      { name: "spot", description: "Spot bid/ask" },
      { name: "forward", description: "Forward bid/ask" },
    ],
  },
  {
    id: "forward-monthly",
    group: "Forward Rates",
    name: "Monthly Forward Rates",
    shortName: "Monthly Forward",
    method: "GET",
    minPlan: "PRO",
    accessFeature: "forward_rates",
    playground: true,
    description: "1M–12M forward ladder. Requires Forward Rate add-on.",
    pathTemplate: "/api/forex/forward-rate/monthly",
    buildPath: () => "/api/forex/forward-rate/monthly",
    pathParams: [],
    queryParams: [
      { key: "pair", label: "pair", defaultValue: "USDINR" },
    ],
    sampleOutput: { pair: "USDINR", tenors: [] },
    inputParams: [
      { name: "pair", required: true, description: "e.g. USDINR" },
      apiKeyParam,
    ],
    outputParams: [{ name: "tenors", description: "Monthly ladder points" }],
  },
  {
    id: "cash-tom-spot",
    group: "Forward Rates",
    name: "Cash / Tom / Spot",
    shortName: "Cash / Tom / Spot",
    method: "GET",
    minPlan: "PRO",
    accessFeature: "forward_rates",
    playground: true,
    description: "Cash, Tom, and Spot points. Requires Forward Rate add-on.",
    pathTemplate: "/api/forex/cash-tom-spot",
    buildPath: () => "/api/forex/cash-tom-spot",
    pathParams: [],
    queryParams: [
      { key: "pair", label: "pair", defaultValue: "USDINR" },
    ],
    sampleOutput: { pair: "USDINR", cash: {}, tom: {}, spot: {} },
    inputParams: [
      { name: "pair", required: true, description: "e.g. USDINR" },
      apiKeyParam,
    ],
    outputParams: [
      { name: "cash", description: "Cash point" },
      { name: "tom", description: "Tom point" },
      { name: "spot", description: "Spot point" },
    ],
  },
];

export function groupPlaygroundEndpoints(endpoints = PLAYGROUND_ENDPOINTS) {
  const groups = [];
  const map = new Map();
  endpoints.forEach((ep) => {
    const g = ep.group || "Other";
    if (!map.has(g)) {
      map.set(g, []);
      groups.push(g);
    }
    map.get(g).push(ep);
  });
  return groups.map((name) => ({ name, items: map.get(name) }));
}
